'use client'

import { useEffect, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import {
  ConsentProvider,
  CookieBanner,
  useCookieConsent,
} from '@alvaromartinezreche98-svg/cookie-consent'
import '@alvaromartinezreche98-svg/cookie-consent/styles.css'

/**
 * Banner mounted manually (provider runs with disableAutoRender so the
 * library doesn't paint it during the LCP window). Stays hidden until:
 *  1. Page has loaded + a settle buffer has elapsed
 *  2. The consent state has hydrated
 *  3. The user hasn't already consented
 *
 * That last check is the dismissal: when the user clicks Accept/Reject,
 * `hasConsented` flips and this returns null — the banner disappears.
 */
function DeferredBanner({ delayMs = 3500 }: { delayMs?: number }) {
  const { hasConsented, isHydrating } = useCookieConsent()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const reveal = () => setTimeout(() => setVisible(true), delayMs)
    if (document.readyState === 'complete') reveal()
    else window.addEventListener('load', reveal, { once: true })
  }, [delayMs])

  if (!visible || isHydrating || hasConsented) return null
  return <CookieBanner />
}

type Props = {
  children: React.ReactNode
  ga4?: string
  plausibleDomain?: string
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export default function ConsentClient({ children, ga4, plausibleDomain }: Props) {
  const locale = useLocale()
  const t = useTranslations('cookies')

  // Brand-aligned theme: --cc-primary stays neutral (Earth Brown) so Accept
  // and Reject have equal visual weight; brand greens go on --cc-accent
  // (toggle on-state). Compliance requirement, see skill compliance.md.
  const theme: Record<`--cc-${string}`, string> = {
    '--cc-surface': 'var(--bg-raised)',
    '--cc-surface-muted': 'var(--bg-sunken)',
    '--cc-overlay': 'var(--overlay-scrim)',
    '--cc-text': 'var(--text-primary)',
    '--cc-text-muted': 'var(--text-secondary)',
    '--cc-text-inverse': 'var(--text-inverse)',
    '--cc-border': 'var(--border-subtle)',
    '--cc-border-strong': 'var(--border-strong)',
    '--cc-primary': 'var(--earth-700)',
    '--cc-primary-hover': 'var(--earth-600)',
    '--cc-primary-text': 'var(--surface-1-sky)',
    '--cc-secondary': 'var(--bg-raised)',
    '--cc-secondary-hover': 'var(--bg-sunken)',
    '--cc-secondary-text': 'var(--text-primary)',
    '--cc-secondary-border': 'var(--border-strong)',
    '--cc-ghost-text': 'var(--text-primary)',
    '--cc-ghost-hover-bg': 'var(--bg-sunken)',
    '--cc-accent': 'var(--olive-500)',
    '--cc-accent-contrast': 'var(--surface-1-sky)',
    '--cc-focus-ring': 'var(--shadow-ring-focus)',
    '--cc-radius-sm': 'var(--radius-xs)',
    '--cc-radius-md': 'var(--radius-sm)',
    '--cc-radius-lg': 'var(--radius-md)',
    '--cc-font-family': 'var(--font-body)',
  }

  const messages = {
    [locale]: {
      bannerTitle: t('bannerTitle'),
      bannerBody: t('bannerBody'),
      acceptAll: t('acceptAll'),
      rejectAll: t('rejectAll'),
      customise: t('customise'),
      modalTitle: t('modalTitle'),
      modalIntro: t('modalIntro'),
      modalSave: t('modalSave'),
      modalClose: t('modalClose'),
      requiredBadge: t('requiredBadge'),
    },
  }

  return (
    <ConsentProvider
      locale={locale}
      defaultLocale="en"
      messages={messages}
      theme={theme}
      disableAutoRender={true}
      categories={[
        { id: 'necessary', label: t('cat.necessary.label'), description: t('cat.necessary.desc'), required: true },
        { id: 'analytics', label: t('cat.analytics.label'), description: t('cat.analytics.desc') },
        { id: 'marketing', label: t('cat.marketing.label'), description: t('cat.marketing.desc') },
      ]}
      onConsentChange={(prefs) => {
        if (typeof window === 'undefined') return
        // GTM Consent Mode v2 — works whether or not GTM is on the page;
        // gtag() reads from dataLayer once it loads.
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push('consent', 'update', {
          ad_storage: prefs.marketing ? 'granted' : 'denied',
          ad_user_data: prefs.marketing ? 'granted' : 'denied',
          ad_personalization: prefs.marketing ? 'granted' : 'denied',
          analytics_storage: prefs.analytics ? 'granted' : 'denied',
        })
        // Plausible / GA tags rendered by ConsentScripts re-mount when
        // localStorage flag flips — broadcast a custom event so it can react.
        window.dispatchEvent(
          new CustomEvent('cc-consent:change', { detail: prefs }),
        )
      }}
    >
      {children}
      <DeferredBanner />
    </ConsentProvider>
  )
}

export type { Props as ConsentClientProps }
